import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useRolesApi } from "../../http/roles/api";
import { Role } from "../../http/roles/types";
import { PaginationQuery } from "../../types";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction } from "../ui/ListItemMenu";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import DeleteRoleDialog from "./DeleteRoleDialog";

const Roles: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const rolesApi = useRolesApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const navigate = useNavigate();

  const [itemToDelete, setItemToDelete] = useState<Role>();

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const result = rolesApi.useAdminRoles(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: Role): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.roles.update) {
      actions.push(getEditAction(i18n, () => navigate(`/admin/roles/${item.id}`)));
    }

    if (userSettings.permissions.roles.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <Header title={i18n.t.roles.plural} />
      <Panel>
        <Table<Role>
          buttons={{
            main: userSettings.permissions.roles.insert && (
              <Button buttonProps={{ onClick: () => navigate("/admin/roles/new") }} glyph={SvgAdd} />
            ),
          }}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          filters={filters}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="type.name" order={orderOptions} />
              {(userSettings.permissions.roles.update || userSettings.permissions.roles.delete) && <th></th>}
            </>
          }
          onRowClick={(item) => {
            navigate(`/admin/roles/${item.id}`);
          }}
          renderRow={(item) => (
            <>
              <td>{item.name}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>

      {!!itemToDelete && <DeleteRoleDialog role={itemToDelete} onClose={() => setItemToDelete(undefined)} />}
    </>
  );
};

export default Roles;
